/**
 * Content label mapping utilities
 *
 * Maps content labels from AT Protocol, Mastodon, and ActivityPub sources
 * to a unified ContentWarning shape used by the UI.
 */

import type { Post } from '@/types/posts'
import { strings } from '@/strings'

type ContentWarning = NonNullable<Post['contentWarning']>

/** AT Protocol label values that map to media-scope warnings */
const AT_MEDIA_LABELS: Record<string, string> = {
    porn: 'Adult Content',
    sexual: 'Sexually Suggestive',
    nudity: 'Nudity',
    nsfl: 'Not Safe For Life',
    gore: 'Graphic Violence',
    'graphic-media': 'Graphic Media',
}

/**
 * Map AT Protocol PostView labels to a content warning.
 * Used for Bluesky SDK paths where labels are hydrated on the PostView.
 */
export function contentWarningFromATLabels(
    labels?: { val: string; neg?: boolean }[]
): ContentWarning | undefined {
    if (!labels?.length) return undefined

    for (const label of labels) {
        if (label.neg) continue

        const mediaText = AT_MEDIA_LABELS[label.val]
        if (mediaText) {
            return { text: mediaText, scope: 'media' }
        }

        if (label.val.startsWith('!warn')) {
            return { text: strings.contentWarnings.contentWarning, scope: 'content' }
        }
    }

    return undefined
}

/**
 * Map AT Protocol self-labels from raw record value.
 * Used for GraphQL/PDS paths where we have the raw record but no hydrated labels.
 */
export function contentWarningFromSelfLabels(
    value: Record<string, unknown>
): ContentWarning | undefined {
    const labels = value.labels as { values?: { val: string }[] } | undefined
    if (!labels?.values?.length) return undefined

    for (const label of labels.values) {
        const mediaText = AT_MEDIA_LABELS[label.val]
        if (mediaText) {
            return { text: mediaText, scope: 'media' }
        }
        if (label.val.startsWith('!warn')) {
            return { text: strings.contentWarnings.contentWarning, scope: 'content' }
        }
    }

    return undefined
}

/**
 * Map Mastodon status fields to a content warning.
 * spoiler_text produces a content-scope warning (hides text + media).
 * sensitive-only produces a media-scope warning.
 */
export function contentWarningFromMastodon(
    sensitive?: boolean,
    spoilerText?: string
): ContentWarning | undefined {
    if (spoilerText) {
        return { text: spoilerText, scope: 'content' }
    }
    if (sensitive) {
        return { text: strings.contentWarnings.sensitiveContent, scope: 'media' }
    }
    return undefined
}

/**
 * Map ActivityPub fields to a content warning.
 * summary (Mastodon spoiler_text equivalent) produces a content-scope warning.
 * sensitive-only produces a media-scope warning.
 */
export function contentWarningFromActivityPub(
    sensitive?: boolean,
    summary?: string | null
): ContentWarning | undefined {
    if (summary) {
        return { text: summary, scope: 'content' }
    }
    if (sensitive) {
        return { text: strings.contentWarnings.sensitiveContent, scope: 'media' }
    }
    return undefined
}
